import { Fragment, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import type { DeliveryProofFormData } from '@/features/delivery-proof/stores/deliveryProofStore';
import { DeliveryProofElementType, DeliveryProofVehicleInfoType } from '@/features/delivery-proof/types/delivery-proof.types';
import { getAttachmentPages } from '@/features/delivery-proof/utils/attachments';
import type { AvailableWarranty } from '@/features/delivery-proof/hooks/useAvailableWarranties';
import { getCompanyIdLabel } from '@/utils/companyId';

/** Partial beneficiary — may come from ContractListItem (fewer fields) or full DP response */
interface PreviewBeneficiary {
  firstname?: string;
  lastname?: string;
  firstName?: string;
  lastName?: string;
  societyName?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  email?: string;
}

/** Partial vehicle — may come from ContractListItem (fewer fields) or full DP response */
interface PreviewVehicle {
  registration?: string;
  brand?: string;
  model?: string;
  eis?: string;
  kmStart?: number;
  deliveryDate?: string;
  vendorType?: number;
}

interface DeliveryProofHtmlPreviewProps {
  formData: DeliveryProofFormData;
  beneficiary?: PreviewBeneficiary;
  vehicle?: PreviewVehicle;
  companyName?: string;
  companySiren?: string;
  companyAddress?: string;
  companyCpCity?: string;
  companyPhone?: string;
  companyEmail?: string;
  vendorType?: string;
  logoUrl?: string | null;
  availableWarranties?: AvailableWarranty[];
  currentStep?: number;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR');
  } catch {
    return '—';
  }
}

function formatKm(value?: number | string | null): string {
  if (value === undefined || value === null || value === '') return '—';
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(num)) return '—';
  return `${num.toLocaleString('fr-FR')} km`;
}

function formatPrice(value?: number | string | null): string {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return `${num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € TTC`;
}

const ELEMENT_LABELS: Record<string, string> = {
  mot: 'Contrôle technique',
  card: 'Carte grise ou certificat provisoire d\'immatriculation',
  diagnostic: 'Rapport de diagnostic',
  histovec: 'Rapport HistoVec',
  key: 'Double des clés',
  nut: 'Écrou antivol',
  record: 'Carnet d\'entretien',
  proof: 'Attestation de travaux',
  wheel: 'Roue de secours - kit de réparation roue',
};

// Libellé de la précision libre (`note`) des éléments qui en proposent une,
// même format que l'API sur la ligne de l'élément
const ELEMENT_NOTE_LABELS: Partial<Record<string, string>> = {
  [DeliveryProofElementType.NUT]: 'emplacement',
};

// Annexes en pleine page (documents A4), même règle que l'API ; les autres
// (photos) passent à trois par page
const FULL_PAGE_ANNEX_KEYS: string[] = [
  DeliveryProofElementType.MOT,
  DeliveryProofElementType.CARD,
  DeliveryProofElementType.DIAGNOSTIC,
  DeliveryProofElementType.HISTOVEC,
];

const HIGHLIGHT_STYLE: React.CSSProperties = {
  outline: '2px solid #8074af',
  outlineOffset: -1,
  borderRadius: 3,
};

/** Native A4 dimensions at 96dpi: 210mm = 794px, 297mm = 1123px */
const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const PAGE_STYLE: React.CSSProperties = {
  fontFamily: "'Roboto Condensed', 'Inter', -apple-system, sans-serif",
  color: '#2e2e2e',
  fontSize: '9pt',
  lineHeight: 1.35,
  background: '#fff',
  width: A4_WIDTH,
  height: A4_HEIGHT,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
};



const MEASURE_STYLE: React.CSSProperties = {
  position: 'absolute',
  visibility: 'hidden',
  width: A4_WIDTH,
  fontFamily: "'Roboto Condensed', 'Inter', -apple-system, sans-serif",
  fontSize: '9pt',
  lineHeight: 1.35,
  color: '#2e2e2e',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'none',
};

const FOOTER_HEIGHT = 30;
const PAGE_CONTENT_MAX = A4_HEIGHT - FOOTER_HEIGHT;

interface AnnexeItem {
  key: string;
  text: string;
  fileUrl: string;
}

/**
 * HTML preview of delivery proof PDF — rendered client-side.
 * Reproduces the structure of delivery_proof.html EJS template 1:1.
 * Multi-page: content pages (auto-paginated) + annexe pages + signature page.
 */
export function DeliveryProofHtmlPreview({
  formData,
  beneficiary,
  vehicle,
  companyName,
  companySiren,
  companyAddress,
  companyCpCity,
  companyPhone,
  companyEmail,
  vendorType = 'Vendeur',
  logoUrl,
  availableWarranties,
  currentStep,
}: DeliveryProofHtmlPreviewProps) {
  const step1Highlight = currentStep === 1 ? HIGHLIGHT_STYLE : undefined;
  const step2Highlight = currentStep === 2 ? HIGHLIGHT_STYLE : undefined;
  const step3Highlight = currentStep === 3 ? HIGHLIGHT_STYLE : undefined;
  const step4Highlight = currentStep === 4 ? HIGHLIGHT_STYLE : undefined;

  // Resolve warranty details from availableWarranties
  const resolvedWarranties = useMemo(() => {
    return formData.warranties.map((w) => {
      if (w.isExternal) {
        return {
          name: w.externalName || '',
          kmLimit: w.externalKmLimit ? `${Number(w.externalKmLimit).toLocaleString('fr-FR')} km` : '',
          financialCap: w.externalFinancialCap ? `${Number(w.externalFinancialCap).toLocaleString('fr-FR')} €` : '',
          duration: w.externalDuration ? `${w.externalDuration} mois` : '',
          pieces: w.externalPieces ? `${w.externalPieces} pièces couvertes` : '',
          price: formatPrice(w.externalPrice),
          isAccepted: w.isAccepted,
        };
      }
      const aw = availableWarranties?.find((a) => a.companyContractId === w.warrantyId);
      return {
        name: aw?.name || `Garantie #${w.warrantyId || '?'}`,
        kmLimit: aw?.kmLimit ? `${aw.kmLimit.toLocaleString('fr-FR')} km` : '',
        financialCap: aw?.financialCap ? `${aw.financialCap.toLocaleString('fr-FR')} €` : '',
        duration: aw?.duration ? `${aw.duration} mois` : '',
        pieces: aw?.partsCount ? `${aw.partsCount} pièces couvertes` : '',
        price: aw?.sellingPrice ? formatPrice(aw.sellingPrice) : '',
        isAccepted: w.isAccepted,
      };
    });
  }, [formData.warranties, availableWarranties]);

  const acceptedWarranty = resolvedWarranties.find((w) => w.isAccepted);
  const proposedWarranties = resolvedWarranties.filter((w) => !w.isAccepted);
  const hasWarranties = formData.warranties.length > 0;

  // Elements
  const hasElements = formData.elements.length > 0;

  // Vehicle info
  const kmInfo = formData.vehicleInfo.find((vi) => vi.type === DeliveryProofVehicleInfoType.KM);
  const presentationInfos = formData.vehicleInfo
    .filter((vi) => vi.type === DeliveryProofVehicleInfoType.PRESENTATION)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const observationInfos = formData.vehicleInfo
    .filter((vi) => vi.type === DeliveryProofVehicleInfoType.OBSERVATION)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const hasVehicleInfo = !!kmInfo || presentationInfos.length > 0 || observationInfos.length > 0;

  const beneficiaryFullname = beneficiary
    ? `${beneficiary.firstname || beneficiary.firstName || ''} ${beneficiary.lastname || beneficiary.lastName || ''}`.trim()
    : '—';
  const beneficiaryCpCity = beneficiary
    ? [beneficiary.postalCode, beneficiary.city].filter(Boolean).join(' ')
    : '—';

  // Build annexes list (same logic as API: one annex per page of each element
  // and observation attachment)
  const annexes = useMemo(() => {
    const items: AnnexeItem[] = [];
    const pushPages = (key: string, text: string, attachment: { fileUrl?: string; filePages?: string[] }) => {
      const pages = getAttachmentPages(attachment);
      pages.forEach((fileUrl, index) => {
        items.push({
          key,
          text: pages.length > 1 ? `${text} (${index + 1}/${pages.length})` : text,
          fileUrl,
        });
      });
    };
    formData.elements.forEach((el) => {
      const text = el.type === DeliveryProofElementType.CUSTOM
        ? (el.label || 'Élément personnalisé')
        : (ELEMENT_LABELS[el.type] || el.type);
      pushPages(el.type, text, el);
    });
    formData.vehicleInfo
      .filter((vi) => vi.type === DeliveryProofVehicleInfoType.OBSERVATION)
      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
      .forEach((vi) => pushPages('observation', vi.value || 'Observation', vi));
    return items;
  }, [formData.elements, formData.vehicleInfo]);

  // Group annexes into pages: documents = full page each, others = 3 per page
  const annexePages = useMemo(() => {
    if (annexes.length === 0) return [];
    const pages: AnnexeItem[][] = [];
    let currentSmallPage: AnnexeItem[] = [];

    annexes.forEach((item) => {
      if (FULL_PAGE_ANNEX_KEYS.includes(item.key)) {
        if (currentSmallPage.length > 0) {
          pages.push(currentSmallPage);
          currentSmallPage = [];
        }
        pages.push([item]);
      } else {
        currentSmallPage.push(item);
        if (currentSmallPage.length === 3) {
          pages.push(currentSmallPage);
          currentSmallPage = [];
        }
      }
    });
    if (currentSmallPage.length > 0) {
      pages.push(currentSmallPage);
    }
    return pages;
  }, [annexes]);

  // ── Build ordered list of content sections ──
  const sections: React.ReactNode[] = [];

  // 0: Header (logo + title banner)
  sections.push(
    <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
      {logoUrl && (
        <div style={{ width: 120, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <img src={logoUrl} alt="Logo" style={{ maxWidth: 120, maxHeight: 60, background: '#fff' }} />
        </div>
      )}
      <div style={{
        flex: 1,
        background: '#2e2e2e',
        color: '#fff',
        textAlign: 'center',
        padding: '10px 8px',
        textTransform: 'uppercase',
        fontWeight: 700,
        fontSize: '16pt',
        letterSpacing: '0.5px',
      }}>
        Attestation de livraison
      </div>
    </div>,
  );

  // 1: Introduction
  sections.push(
    <div style={{ padding: '0 20px', marginTop: 10, fontSize: '9pt', lineHeight: 1.4, textAlign: 'justify' }}>
      Cette attestation a pour but de définir et d&apos;historiser les conditions dans lesquelles l&apos;Acheteur
      a pris possession du Véhicule auprès de son {vendorType} le jour de la livraison.
      Les informations listées dans ce document ne sont pas exhaustives.
    </div>,
  );

  // 2: Reference bar
  sections.push(
    <div style={{
      margin: '15px 20px 0',
      padding: '10px',
      background: '#e8e8e8',
      textAlign: 'center',
      fontSize: '9pt',
      display: 'flex',
      justifyContent: 'center',
      gap: 25,
      ...step1Highlight,
    }}>
      <span>N° du contrat : <strong>{formData.contractNum || '—'}</strong></span>
      {formData.dpNumber && (
        <span>N° du bon de commande : <strong>{formData.dpNumber}</strong></span>
      )}
      <span>Date de livraison : <strong>{formatDate(vehicle?.deliveryDate)}</strong></span>
    </div>,
  );

  // 3: Parties (vendeur + acheteur)
  sections.push(
    <div style={{ margin: '20px 20px 0', display: 'flex', gap: 0 }}>
      <div style={{ flex: 1 }}>
        <SectionHeader text={vendorType} />
        <PartyTable rows={[
          ['Raison sociale', companyName || '—'],
          [getCompanyIdLabel(companySiren), companySiren || '—'],
          ['Adresse', companyAddress || '—'],
          ['', companyCpCity || ''],
          ...(companyPhone ? [['Téléphone', companyPhone] as [string, string]] : []),
          ...(companyEmail ? [['Email', companyEmail] as [string, string]] : []),
        ]} />
      </div>
      <div style={{ width: 2, background: '#d0d0d0', flexShrink: 0 }} />
      <div style={{ flex: 1, ...step1Highlight }}>
        <SectionHeader text="Acheteur" />
        <PartyTable rows={[
          ...(beneficiary?.societyName ? [['Société', beneficiary.societyName] as [string, string]] : []),
          ['Nom', beneficiaryFullname],
          ['Adresse', beneficiary?.address || '—'],
          ['', beneficiaryCpCity],
          ['Téléphone', beneficiary?.phone || '—'],
          ['Email', beneficiary?.email || '—'],
        ]} />
      </div>
    </div>,
  );

  // 4: Vehicle
  sections.push(
    <div style={{ margin: '20px 20px 0', ...step1Highlight }}>
      <SectionHeader text="Véhicule" />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <VehicleRow label="Marque" value={vehicle?.brand || '—'} />
          <VehicleRow label="Modèle" value={vehicle?.model || '—'} />
          <VehicleRow label="Immatriculation" value={vehicle?.registration || '—'} />
          <VehicleRow label="Date de mise en circulation" value={formatDate(vehicle?.eis)} />
          {vehicle?.kmStart !== undefined && vehicle?.kmStart !== null && (
            <VehicleRow label="Kilométrage" value={formatKm(vehicle.kmStart)} />
          )}
        </tbody>
      </table>
    </div>,
  );

  // 5: Warranties (conditional)
  if (hasWarranties) {
    sections.push(
      <div style={{ margin: '25px 20px 0', padding: '0 30px', ...step2Highlight }}>
        <SectionHeader text="Garanties commerciales" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 15, fontSize: '9pt' }}>
          <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e' }} />
          <span>Garantie(s) commerciale(s) proposée(s) à l&apos;Acheteur en complément de l&apos;achat du Véhicule</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 220px))', gap: 10, marginTop: 15 }}>
          {acceptedWarranty && (
            <WarrantyCard warranty={acceptedWarranty} hidePrices={formData.hidePrices} isAccepted />
          )}
          {proposedWarranties.map((w, i) => (
            <WarrantyCard key={i} warranty={w} hidePrices={formData.hidePrices} isAccepted={false} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 25, fontSize: '9pt' }}>
          <span style={{ color: '#2d8a2e', fontWeight: 700, fontSize: '20px' }}>✓</span>
          <span>
            Accepté par l&apos;acheteur en complément de l&apos;achat du véhicule.<br />
            Un contrat a été remis à l&apos;acheteur précisant les conditions de cette garantie commerciale
            et un rappel de ses garanties légales.
          </span>
        </div>
        {proposedWarranties.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 15, fontSize: '9pt' }}>
            <span style={{ color: '#b93a32', fontWeight: 700, fontSize: '20px' }}>✗</span>
            <span>Refusé par l&apos;acheteur en complément de l&apos;achat du véhicule</span>
          </div>
        )}
      </div>,
    );
  }

  // 6: Elements (conditional)
  if (hasElements) {
    sections.push(
      <div style={{ margin: '25px 20px 0', padding: '0 30px', ...step3Highlight }}>
        <SectionHeader text="Éléments remis avec le véhicule" />
        {formData.elements.map((el, i) => {
          const label = el.type === DeliveryProofElementType.CUSTOM
            ? (el.label || 'Élément personnalisé')
            : (ELEMENT_LABELS[el.type] || el.type);
          const noteLabel = ELEMENT_NOTE_LABELS[el.type];
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 10, fontSize: '9pt' }}>
              <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e' }} />
              <span>
                {label}
                {el.note && <> — {noteLabel ? `${noteLabel} : ` : ''}{el.note}</>}
                {el.date && <> du {formatDate(el.date)}</>}
                {el.fileUrl && <span style={{ color: '#2d8a2e', marginLeft: 5 }}>Document en annexe</span>}
              </span>
            </div>
          );
        })}
      </div>,
    );
  }

  // 7: Vehicle state (conditional)
  if (hasVehicleInfo) {
    sections.push(
      <div style={{ margin: '25px 20px 0', padding: '0 30px', ...step4Highlight }}>
        <SectionHeader text="État du véhicule et observations" />

        {/* KM */}
        {kmInfo && (
          <div style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '9pt' }}>
              <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e' }} />
              <span>Kilométrage le jour de la livraison : <strong>{kmInfo.value || '—'} km</strong></span>
            </div>
            {kmInfo.fileUrl && (
              <div style={{ marginLeft: 25, marginTop: 10 }}>
                <img src={kmInfo.fileUrl} alt="compteur" style={{ maxWidth: 300, maxHeight: 150, objectFit: 'contain' }} />
              </div>
            )}
          </div>
        )}

        {/* Presentation */}
        {presentationInfos.length > 0 && (
          <div style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '9pt' }}>
              <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e' }} />
              <span>Présentation générale du véhicule</span>
            </div>
            <div style={{ marginLeft: 25, marginTop: 10, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
              {presentationInfos.map((vi, i) => vi.fileUrl && (
                <div key={i} style={{ width: 220, height: 150, overflow: 'hidden' }}>
                  <img src={vi.fileUrl} alt="présentation" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observations */}
        {observationInfos.length > 0 && (
          <div style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '9pt' }}>
              <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e' }} />
              <span>Observations du vendeur</span>
            </div>
            <ul style={{ margin: '5px 0 0', padding: 0, listStyle: 'disc', paddingLeft: 30, fontSize: '9pt' }}>
              {observationInfos.map((vi, i) => (
                <li key={i}>
                  {vi.value || '—'}
                  {vi.fileUrl && <span style={{ color: '#2d8a2e', marginLeft: 5 }}>Document en annexe</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>,
    );
  }

  // ── Measurement: compute which sections fit on each page ──
  const measureRef = useRef<HTMLDivElement>(null);
  const [contentPages, setContentPages] = useState<number[][] | null>(null);

  useEffect(() => {
    const container = measureRef.current;
    if (!container) return;

    const recompute = () => {
      const children = Array.from(container.children) as HTMLElement[];
      if (children.length === 0) return;

      const pages: number[][] = [];
      let page: number[] = [];
      let h = 0;

      children.forEach((child, i) => {
        const ch = child.offsetHeight;
        if (h + ch > PAGE_CONTENT_MAX && page.length > 0) {
          pages.push(page);
          page = [i];
          h = ch;
        } else {
          page.push(i);
          h += ch;
        }
      });
      if (page.length > 0) pages.push(page);

      setContentPages((prev) => {
        const next = JSON.stringify(pages);
        return JSON.stringify(prev) === next ? prev : pages;
      });
    };

    recompute();

    // Re-measure when any section resizes (e.g. image loads change heights)
    const obs = new ResizeObserver(recompute);
    Array.from(container.children).forEach((c) => obs.observe(c));

    return () => obs.disconnect();
  });

  // ── Scale: measure container width → compute transform scale ──
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    if (containerRef.current) {
      setScale(containerRef.current.offsetWidth / A4_WIDTH);
    }
  }, []);

  useEffect(() => {
    updateScale();
    const obs = new ResizeObserver(updateScale);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [updateScale]);

  // Fallback before measurement: all sections on one page
  const resolvedContentPages = contentPages || [sections.map((_, i) => i)];
  const totalPages = resolvedContentPages.length + annexePages.length + 1;
  const scaledPageHeight = A4_HEIGHT * scale;
  const gapSize = 12 * scale;

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Hidden measurement container — same font/layout as pages but unconstrained height */}
      <div ref={measureRef} aria-hidden style={MEASURE_STYLE}>
        {sections.map((section, i) => (
          <div key={i}>{section}</div>
        ))}
      </div>

      {/* Visible pages */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: gapSize }}>
        {/* ===== CONTENT PAGES (auto-paginated) ===== */}
        {resolvedContentPages.map((indices, pageIdx) => (
          <ScaledPage key={`content-${pageIdx}`} scale={scale} height={scaledPageHeight}>

            {indices.map((i) => (
              <Fragment key={i}>{sections[i]}</Fragment>
            ))}
            <div style={{ flex: 1 }} />
            <PageFooter pageNumber={pageIdx + 1} totalPages={totalPages} />
          </ScaledPage>
        ))}

        {/* ===== ANNEXE PAGES ===== */}
        {annexePages.map((pageItems, pageIndex) => {
          const isFullPage = pageItems.length === 1 && FULL_PAGE_ANNEX_KEYS.includes(pageItems[0].key);
          return (
            <ScaledPage key={`annexe-${pageIndex}`} scale={scale} height={scaledPageHeight}>
  
              <div style={{ padding: '20px 20px 0' }}>
                <SectionHeader text="Annexes" />
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: isFullPage ? 'center' : 'flex-start',
                padding: '20px 20px',
                gap: isFullPage ? 0 : 20,
              }}>
                {pageItems.map((item, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <p style={{ fontWeight: 700, fontSize: '9pt', marginBottom: 8 }}>{item.text}</p>
                    <img
                      src={item.fileUrl}
                      alt="document annexe"
                      style={isFullPage
                        ? { maxWidth: 600, maxHeight: 900, objectFit: 'contain' }
                        : { maxWidth: 300, maxHeight: 450, objectFit: 'contain' }
                      }
                    />
                  </div>
                ))}
              </div>
              <PageFooter pageNumber={resolvedContentPages.length + 1 + pageIndex} totalPages={totalPages} />
            </ScaledPage>
          );
        })}

        {/* ===== SIGNATURE PAGE (always last) ===== */}
        <ScaledPage scale={scale} height={scaledPageHeight}>
          <div style={{ flex: 1 }} />
          <SignatureBlock vendorType={vendorType} />
          <PageFooter pageNumber={totalPages} totalPages={totalPages} />
        </ScaledPage>
      </div>
    </div>
  );
}

/* --- Sub-components --- */

function ScaledPage({ scale, height, children }: { scale: number; height: number; children: React.ReactNode }) {
  return (
    <div style={{ width: '100%', height, overflow: 'hidden', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', background: '#fff' }}>
      <div style={{ ...PAGE_STYLE, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}


function PageFooter({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      borderTop: '1px solid #2e2e2e',
      margin: '0 20px',
      padding: '3px 2px',
      fontSize: '8pt',
      fontWeight: 700,
      flexShrink: 0,
    }}>
      <span>Attestation de livraison</span>
      <span>{pageNumber} / {totalPages}</span>
    </div>
  );
}

function SignatureBlock({ vendorType }: { vendorType: string }) {
  return (
    <div style={{ margin: '0 20px 20px', padding: '0 20px' }}>
      <SectionHeader text="Synthèse et signature" />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 20, padding: '0 30px', fontSize: '9pt', textAlign: 'justify' }}>
        <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e', marginTop: 2 }} />
        <span>
          J&apos;atteste que le Véhicule désigné ci-dessus, dont je prends livraison ce jour, est conforme
          au descriptif fait par le {vendorType} et à son bon usage. En outre, j&apos;affirme avoir contrôlé
          le bon fonctionnement de tous les équipements et accessoires de celui-ci.
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 10, padding: '0 30px', fontSize: '9pt', textAlign: 'justify' }}>
        <input type="checkbox" checked readOnly style={{ accentColor: '#2e2e2e', marginTop: 2 }} />
        <span>
          Je déclare prendre à ma charge, à compter de ce jour, toutes les responsabilités pénales,
          civile et financières pouvant provenir de l&apos;emploi dudit véhicule et renoncer aux dispositions
          l&apos;article 19 de la loi du 13 juillet 1930 relative à l&apos;assurance accidents.
        </span>
      </div>
      <div style={{
        width: '49%',
        marginLeft: 'auto',
        marginTop: 15,
        border: '1px solid #2e2e2e',
        height: 120,
      }}>
        <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '9pt', padding: '5px 10px', textAlign: 'center' }}>
          Signature électronique de l&apos;acheteur
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ text }: { text: string }) {
  return (
    <div style={{
      background: '#e8e8e8',
      color: '#2e2e2e',
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: '10pt',
      padding: '6px 10px',
      textAlign: 'center',
    }}>
      {text}
    </div>
  );
}

function PartyTable({ rows }: { rows: [string, string][] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i}>
            <td style={{ fontSize: '8pt', padding: '3px 8px', textTransform: 'uppercase', fontWeight: label ? 600 : 400, width: '40%', verticalAlign: 'top', color: '#666' }}>
              {label ? `${label} :` : ''}
            </td>
            <td style={{ fontSize: '8pt', padding: '3px 8px', textTransform: label === 'Email' ? 'none' : 'uppercase', verticalAlign: 'top' }}>
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VehicleRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td style={{ fontSize: '9pt', padding: '4px 10px', fontWeight: 700, width: '45%', verticalAlign: 'top' }}>
        {label} :
      </td>
      <td style={{ fontSize: '9pt', padding: '4px 10px', verticalAlign: 'top' }}>
        {value}
      </td>
    </tr>
  );
}

interface ResolvedWarranty {
  name: string;
  kmLimit: string;
  financialCap: string;
  duration: string;
  pieces: string;
  price: string;
  isAccepted: boolean;
}

function WarrantyCard({ warranty, hidePrices, isAccepted }: { warranty: ResolvedWarranty; hidePrices: boolean; isAccepted: boolean }) {
  return (
    <div style={{
      border: '1px solid #2e2e2e',
      borderRadius: 10,
      padding: '15px 10px 15px',
      fontSize: '8pt',
      position: 'relative',
    }}>
      {warranty.name && <div style={{ fontWeight: 700, marginBottom: 4, minHeight: 30 }}>{warranty.name}</div>}
      {warranty.kmLimit && <div>Limite km : {warranty.kmLimit}</div>}
      {warranty.financialCap && <div>Plafond : {warranty.financialCap}</div>}
      {warranty.duration && <div>Durée : {warranty.duration}</div>}
      {warranty.pieces && <div>Pièces :{warranty.pieces}</div>}
      {!hidePrices && warranty.price && <div style={{ fontWeight: 700, marginTop: 5 }}>Prix de vente : {warranty.price}</div>}
      <div style={{
        position: 'absolute',
        bottom: 8,
        right: 10,
        fontSize: '32px',
        fontWeight: 700,
      }}>
        {isAccepted
          ? <span style={{ color: '#2d8a2e' }}>✓</span>
          : <span style={{ color: '#b93a32' }}>✗</span>
        }
      </div>
    </div>
  );
}
